/**
 * Image Upload Utilities
 *
 * Provides image-specific validation and configuration resolution.
 * Extends base media upload validation with magic bytes and per-type size limits.
 * Handles tier-based configuration overrides following precedence: base → type → tier → programmatic.
 */

import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import { validateFile } from '@/lib/mediaUploadUtils'
import type { UploaderConfig } from '@/lib/mediaUploadTypes'
import { validateMagicBytes } from '@/lib/upload/magicBytes'
import type {
  ImageUploadConfig,
  ValidationResult,
  TierConfig,
  TypeSpecificConfig,
} from '@/lib/imageUploadTypes'

// ============================================================================
// Logger (Lazy Initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('lib/imageUploadUtils')
  }
  return _log
}

// ============================================================================
// File Validation
// ============================================================================

/**
 * Validate an image file with all image-specific checks.
 *
 * Validation flow:
 * 1. Base validation (extension, MIME type from mediaUploadUtils)
 * 2. Magic bytes validation (verify file content matches declared type)
 * 3. Per-type size limit validation (check against type-specific limits if configured)
 *
 * Returns first error encountered (fail-fast approach).
 *
 * @param file - The File object to validate
 * @param config - Image upload configuration with type-specific limits
 * @returns Promise resolving to ValidationResult
 *
 * @example
 * const result = await validateImageFile(file, config)
 * if (!result.valid) {
 *   toast.error(result.error?.message)
 * }
 */
export async function validateImageFile(
  file: File,
  config: ImageUploadConfig
): Promise<ValidationResult> {
  const log = getLog()

  // Step 1: Base validation (extension, MIME type)
  const baseResult = validateFile(file, config)
  if (!baseResult.valid) {
    log.debug(`Base validation failed for ${file.name}: ${baseResult.errorCode}`)
    return {
      valid: false,
      error: {
        code: baseResult.errorCode || 'INVALID_FILE_TYPE',
        message:
          baseResult.message || 'This file type is not allowed',
      },
    }
  }

  // Step 2: Magic bytes validation (deep type check)
  const magicResult = await validateMagicBytes(file)
  if (!magicResult.valid) {
    log.debug(`Magic bytes validation failed for ${file.name}`)
    return magicResult
  }

  // Step 3: Per-type size limit validation
  const typeConfig = config.typeConfig?.[file.type]
  const applicableLimit = typeConfig?.maxFileSize ?? config.maxFileSize

  // Special handling for "unlimited" string
  if (applicableLimit !== 'unlimited' && typeof applicableLimit === 'number') {
    if (file.size > applicableLimit) {
      const sizeMB = (applicableLimit / 1024 / 1024).toFixed(2)
      const message = `File exceeds the ${file.type} size limit of ${sizeMB}MB`
      log.debug(`Size validation failed for ${file.name}: ${message}`)
      return {
        valid: false,
        error: {
          code: 'FILE_TOO_LARGE_FOR_TYPE',
          message,
        },
      }
    }
  }

  log.debug(`All validations passed for ${file.name}`)
  return { valid: true }
}

// ============================================================================
// Configuration Resolution
// ============================================================================

/**
 * Deep merge two configuration objects.
 * Values from the source override values from the base.
 *
 * @param base - Base configuration object
 * @param source - Configuration to merge on top
 * @returns Merged configuration
 */
function mergeConfigs<T extends Record<string, any>>(base: T, source: Partial<T>): T {
  const result: any = { ...base }

  for (const key in source) {
    const sourceValue = source[key]

    // Deep merge for nested objects, but not for arrays
    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      key in result &&
      typeof result[key] === 'object' &&
      !Array.isArray(result[key])
    ) {
      result[key] = mergeConfigs(result[key], sourceValue)
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue
    }
  }

  return result
}

/**
 * Resolve image upload configuration with tier-based overrides.
 *
 * Applies configuration in precedence order:
 * 1. Base config defaults
 * 2. Type-specific overrides (per MIME type)
 * 3. Tier-specific overrides (per tier name)
 * 4. Programmatic prop overrides (last wins)
 *
 * @param rawConfig - Configuration object or YAML string (if string, parse it)
 * @param tier - Optional tier name to apply (e.g., 'free', 'pro')
 * @returns Fully resolved ImageUploadConfig with all overrides applied
 *
 * @example
 * // Basic resolution
 * const config = resolveImageConfig(baseConfig)
 *
 * // With tier override
 * const config = resolveImageConfig(baseConfig, 'pro')
 *
 * // Precedence: base → type → tier → programmatic
 * // Last wins, so tier overrides type, type overrides base, etc.
 */
export function resolveImageConfig(
  rawConfig: string | ImageUploadConfig | undefined,
  tier?: string
): ImageUploadConfig {
  const log = getLog()

  if (!rawConfig) {
    log.warn('No config provided to resolveImageConfig')
    return {} as ImageUploadConfig
  }

  let config: ImageUploadConfig
  if (typeof rawConfig === 'string') {
    try {
      config = JSON.parse(rawConfig) as ImageUploadConfig
      log.debug('Parsed config from JSON string')
    } catch {
      log.warn('Failed to parse config as JSON, treating as empty config')
      config = {} as ImageUploadConfig
    }
  } else {
    config = { ...rawConfig }
  }

  // If no tier specified, return config as-is
  if (!tier || !config.tiers || !config.tiers[tier]) {
    if (tier) {
      log.warn(`Tier '${tier}' not found in config, returning base config`)
    }
    return config
  }

  // Apply tier overrides
  const tierConfig = config.tiers[tier] as TierConfig
  log.debug(`Applying tier overrides for tier: ${tier}`)

  // Merge base + tier config
  const resolved = mergeConfigs(config, {
    maxFileSize: tierConfig.maxFileSize,
    allowedTypes: tierConfig.allowedTypes,
    compression: tierConfig.compression,
    dimensions: tierConfig.dimensions,
  } as Partial<ImageUploadConfig>)

  // Also merge tier-specific typeConfig overrides
  if (tierConfig.typeConfig) {
    const mergedTypeConfig = { ...resolved.typeConfig }
    for (const [mimeType, typeOverride] of Object.entries(tierConfig.typeConfig)) {
      mergedTypeConfig[mimeType] = mergeConfigs(
        mergedTypeConfig[mimeType] || {},
        typeOverride as TypeSpecificConfig
      )
    }
    resolved.typeConfig = mergedTypeConfig
  }

  return resolved
}
